import React, { useEffect, useRef, useState } from 'react';
import AdVisualBackground from '../AdVisualBackground';
import { getTypographyStyle } from '../../utils/campaignPreviewUtils';

export interface InternalCreativeState {
  selectedFont: string;
  headlineFontSize: number;
  benefitFontSize: number;
  descriptionFontSize: number;
  ctaFontSize: number;
  headlineColor: string;
  benefitColor: string;
  descriptionColor: string;
  ctaColor: string;
  headlineAlign: 'left' | 'center' | 'right';
  benefitAlign: 'left' | 'center' | 'right';
  descriptionAlign: 'left' | 'center' | 'right';
  ctaAlign: 'left' | 'center' | 'right';
  headlineBold: boolean;
  benefitBold: boolean;
  descriptionBold: boolean;
  ctaBold: boolean;
  headlineItalic: boolean;
  benefitItalic: boolean;
  descriptionItalic: boolean;
  ctaItalic: boolean;
  headlineShadow: boolean;
  benefitShadow: boolean;
  descriptionShadow: boolean;
  ctaShadow: boolean;
  headlineX: number;
  headlineY: number;
  benefitX: number;
  benefitY: number;
  descriptionX: number;
  descriptionY: number;
  ctaX: number;
  ctaY: number;
  specialEffect: 'none' | 'blur' | 'gradient' | 'shadow';
  blurIntensity: number;
  autoEnhance: boolean;
  useBackgroundOnly: boolean;
  overlayPosition: { x: number; y: number };
  overlaySize: number;
  logoPosition: { x: number; y: number };
  logoSize: number;
}

export interface InternalMediaState {
  adImageUrl: string | null;
  overlayImageUrl: string | null;
  logoUrl: string | null;
}

export type InternalEditableElement = 'headline' | 'benefit' | 'description' | 'cta';

interface InternalAdCanvasViewProps {
  headline: string;
  benefit: string;
  description: string;
  callToAction: string;
  designFramework: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  creative: InternalCreativeState;
  media: InternalMediaState;
  interactive?: boolean;
  canvasRef?: React.Ref<HTMLDivElement>;
  onSelectElement?: (el: InternalEditableElement) => void;
  onOverlayPointerDown?: (e: React.PointerEvent<HTMLDivElement>) => void;
  onLogoPointerDown?: (e: React.PointerEvent<HTMLDivElement>) => void;
}

const layoutSlugByTitle: Record<string, string> = {
  'Vertical Flow': 'vertical-flow',
  'Left Split': 'left-split',
  'Overlay Classic': 'overlay-classic',
  'Headline Impact': 'headline-impact',
  'Quote Bubble': 'quote-bubble',
  'Full Visual': 'full-visual',
};

const getImageOverlayStyles = (framework: string): React.CSSProperties | undefined => {
  switch (framework) {
    case 'overlay-classic':
    case 'vertical-flow':
    case 'left-split':
    case 'headline-impact':
    case 'quote-bubble':
    case 'full-visual':
      return { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: '0.375rem' };
    default:
      return undefined;
  }
};

const hexToRgba = (hex: string, opacity = 1): string => {
  const clean = hex.replace('#', '');
  const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
  const num = parseInt(full, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const translateStyle = (x: number, y: number): React.CSSProperties => ({
  transform: `translate(${(x - 50) * 2}%, ${(y - 50) * 2}%)`,
});

const alignClass = (align: 'left' | 'center' | 'right'): string => {
  if (align === 'left') return 'justify-start';
  if (align === 'right') return 'justify-end';
  return 'justify-center';
};

const InternalAdCanvasView: React.FC<InternalAdCanvasViewProps> = ({
  headline,
  benefit,
  description,
  callToAction,
  designFramework,
  primaryColor,
  secondaryColor,
  accentColor,
  creative,
  media,
  interactive = false,
  canvasRef,
  onSelectElement,
  onOverlayPointerDown,
  onLogoPointerDown,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [previewScale, setPreviewScale] = useState(1);

  const selectedLayoutStyle = layoutSlugByTitle[designFramework] ?? 'vertical-flow';
  const typography = {
    selectedFont: creative.selectedFont,
    headlineFontSize: creative.headlineFontSize,
    headlineColor: creative.headlineColor,
    headlineAlign: creative.headlineAlign,
    headlineBold: creative.headlineBold,
    headlineItalic: creative.headlineItalic,
    headlineShadow: creative.headlineShadow,
    benefitFontSize: creative.benefitFontSize,
    benefitColor: creative.benefitColor,
    benefitAlign: creative.benefitAlign,
    benefitBold: creative.benefitBold,
    benefitItalic: creative.benefitItalic,
    benefitShadow: creative.benefitShadow,
    descriptionFontSize: creative.descriptionFontSize,
    descriptionColor: creative.descriptionColor,
    descriptionAlign: creative.descriptionAlign,
    descriptionBold: creative.descriptionBold,
    descriptionItalic: creative.descriptionItalic,
    descriptionShadow: creative.descriptionShadow,
    ctaFontSize: creative.ctaFontSize,
    ctaColor: creative.ctaColor,
    ctaAlign: creative.ctaAlign,
    ctaBold: creative.ctaBold,
    ctaItalic: creative.ctaItalic,
    ctaShadow: creative.ctaShadow,
  };
  const brandColors = { primary: primaryColor, secondary: secondaryColor, accent: accentColor };

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const calculateScale = () => {
      const wrapperWidth = el.offsetWidth;
      const wrapperHeight = el.offsetHeight;
      const scaleX = wrapperWidth / 640;
      const scaleY = wrapperHeight / 720;
      const scale = Math.min(scaleX, scaleY, 1.5);
      setPreviewScale(Math.max(0.3, scale));
    };
    calculateScale();
    const observer = new ResizeObserver(calculateScale);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const clickable = (element: InternalEditableElement, extra: string = '') =>
    `${interactive ? 'cursor-pointer' : ''}${extra ? ` ${extra}` : ''}`;

  const renderLayout = () => {
    switch (selectedLayoutStyle) {
      case 'vertical-flow':
        return (
          <div className="relative flex h-full flex-col min-h-0">
            <div className="relative flex flex-shrink items-center justify-center overflow-hidden rounded" style={{ minHeight: '40%', maxHeight: '66.67%', flex: '1 1 auto' }}>
              {creative.specialEffect === 'blur' && (
                <div className="absolute inset-0 z-10" style={{ backgroundImage: `url(${media.adImageUrl ?? ''})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: `blur(${creative.blurIntensity}px) brightness(1.1)`, transform: 'scale(1.1)' }}></div>
              )}
              {creative.specialEffect === 'gradient' && <div className="absolute inset-0 bg-gradient-to-br from-blue-600/30 via-purple-600/30 to-pink-600/30 z-10"></div>}
              {creative.specialEffect === 'shadow' && <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>}
              {creative.specialEffect !== 'blur' && <div className="absolute inset-0 bg-opacity-30 z-10"></div>}
              {creative.specialEffect === 'blur' && <div className="absolute inset-0 bg-black bg-opacity-20 z-10"></div>}
              {media.adImageUrl ? (
                <img src={media.adImageUrl} crossOrigin="anonymous" alt="" className="h-full w-full object-cover rounded" style={{ filter: creative.autoEnhance ? 'brightness(1.2) contrast(1.1) saturate(1.1)' : 'none' }} />
              ) : null}
              {media.adImageUrl ? <div style={getImageOverlayStyles('vertical-flow')} /> : <div className="h-full w-full rounded" style={creative.useBackgroundOnly ? { backgroundColor: primaryColor || '#0066FF' } : {}} />}
            </div>
            <div id="text-content" className="z-20 flex flex-grow flex-col p-2" style={{ backgroundColor: primaryColor || '#0066FF', minHeight: '33.33%' }}>
              <h1
                onClick={interactive ? () => onSelectElement?.('headline') : undefined}
                className={clickable('headline')}
                style={{
                  ...getTypographyStyle('headline', typography, 1, brandColors),
                  fontWeight: creative.headlineBold ? '700' : '600',
                  lineHeight: 1.2,
                  textAlign: creative.headlineAlign,
                  color: creative.headlineColor || getTypographyStyle('headline', typography, 1, brandColors).color,
                  textShadow: creative.headlineShadow ? '2px 2px 4px rgba(0,0,0,0.8)' : 'none',
                  ...translateStyle(creative.headlineX, creative.headlineY),
                  userSelect: 'none', whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word',
                }}
              >
                {headline}
              </h1>
              <p onClick={interactive ? () => onSelectElement?.('benefit') : undefined} className={`${clickable('benefit')} mb-1`} style={{ ...getTypographyStyle('benefit', typography, 1, brandColors), lineHeight: 1.3, textAlign: creative.benefitAlign, color: creative.benefitColor || getTypographyStyle('benefit', typography, 1, brandColors).color, textShadow: creative.benefitShadow ? '1px 1px 2px rgba(0,0,0,0.8)' : 'none', ...translateStyle(creative.benefitX, creative.benefitY), userSelect: 'none', whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                {benefit}
              </p>
              <p onClick={interactive ? () => onSelectElement?.('description') : undefined} className={`${clickable('description')} mb-2 opacity-90`} style={{ ...getTypographyStyle('description', typography, 1, brandColors), lineHeight: 1.2, textAlign: creative.descriptionAlign, color: creative.descriptionColor || getTypographyStyle('description', typography, 1, brandColors).color, textShadow: creative.descriptionShadow ? '1px 1px 2px rgba(0,0,0,0.8)' : 'none', ...translateStyle(creative.descriptionX, creative.descriptionY), userSelect: 'none', whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                {description}
              </p>
              <p onClick={interactive ? () => onSelectElement?.('cta') : undefined} className={`${clickable('cta')} mb-6`} style={{ ...translateStyle(creative.ctaX, creative.ctaY), textAlign: creative.ctaAlign, transformOrigin: 'center', userSelect: 'none' }}>
                <button className="rounded-lg px-6 py-3 shadow-lg" style={{ backgroundColor: accentColor, ...getTypographyStyle('cta', typography, 1, brandColors), fontWeight: creative.ctaBold ? '700' : '600', textShadow: creative.ctaShadow ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none' }}>
                  {callToAction}
                </button>
              </p>
            </div>
          </div>
        );

      case 'left-split':
        return (
          <div className="relative flex h-full">
            <div className="relative flex w-1/2 items-center justify-center overflow-hidden">
              {creative.specialEffect === 'blur' && (
                <div className="absolute inset-0 z-10" style={{ backgroundImage: `url(${media.adImageUrl ?? ''})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: `blur(${creative.blurIntensity}px) brightness(1.1)`, transform: 'scale(1.1)' }}></div>
              )}
              {creative.specialEffect === 'gradient' && <div className="absolute inset-0 bg-gradient-to-br from-blue-600/30 via-purple-600/30 to-pink-600/30 z-10"></div>}
              {creative.specialEffect === 'shadow' && <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>}
              {creative.specialEffect !== 'blur' && <div className="absolute inset-0 bg-opacity-30 z-10"></div>}
              {creative.specialEffect === 'blur' && <div className="absolute inset-0 bg-black bg-opacity-20 z-10"></div>}
              {media.adImageUrl ? <img src={media.adImageUrl} crossOrigin="anonymous" alt="" className="h-full w-full object-cover" style={{ filter: creative.autoEnhance ? 'brightness(1.2) contrast(1.1) saturate(1.1)' : 'none' }} /> : null}
              {media.adImageUrl ? <div style={getImageOverlayStyles('left-split')} /> : <div className="z-0 h-full w-full" style={creative.useBackgroundOnly ? { backgroundColor: primaryColor || '#0066FF' } : { backgroundColor: '#FFFFFF' }} />}
            </div>
            <div className="relative flex w-1/2 flex-col justify-center p-4" style={{ backgroundColor: primaryColor, zIndex: 20 }}>
              <h1 onClick={interactive ? () => onSelectElement?.('headline') : undefined} className={`${clickable('headline')} mb-3`} style={{ ...getTypographyStyle('headline', typography, 0.9, brandColors), lineHeight: 1.1, textAlign: creative.headlineAlign, color: creative.headlineColor || getTypographyStyle('headline', typography, 0.9, brandColors).color, textShadow: creative.headlineShadow ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none', ...translateStyle(creative.headlineX, creative.headlineY), maxWidth: '100%', userSelect: 'none', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                {headline}
              </h1>
              <p onClick={interactive ? () => onSelectElement?.('benefit') : undefined} className={`${clickable('benefit')} mb-2`} style={{ ...getTypographyStyle('benefit', typography, 0.8, brandColors), opacity: 0.95, lineHeight: 1.3, textAlign: creative.benefitAlign, color: creative.benefitColor || getTypographyStyle('benefit', typography, 0.8, brandColors).color, textShadow: creative.benefitShadow ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none', ...translateStyle(creative.benefitX, creative.benefitY), userSelect: 'none', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                {benefit}
              </p>
              <p onClick={interactive ? () => onSelectElement?.('description') : undefined} className={`${clickable('description')} mb-4`} style={{ ...getTypographyStyle('description', typography, 0.7, brandColors), opacity: 0.9, lineHeight: 1.3, textAlign: creative.descriptionAlign, color: creative.descriptionColor || getTypographyStyle('description', typography, 0.7, brandColors).color, textShadow: creative.descriptionShadow ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none', ...translateStyle(creative.descriptionX, creative.descriptionY), userSelect: 'none', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                {description}
              </p>
              <div onClick={interactive ? () => onSelectElement?.('cta') : undefined} className={`${clickable('cta')} flex ${alignClass(creative.ctaAlign)}`} style={{ ...translateStyle(creative.ctaX, creative.ctaY), transformOrigin: 'center' }}>
                <button className="rounded-lg px-4 py-2 shadow-lg" style={{ backgroundColor: accentColor, ...getTypographyStyle('cta', typography, 0.8, brandColors), color: creative.ctaColor || getTypographyStyle('cta', typography, 0.8, brandColors).color, textShadow: creative.ctaShadow ? '1px 1px 2px rgba(0,0,0,0.3)' : 'none', border: '2px solid rgba(255,255,255,0.2)' }}>
                  {callToAction}
                </button>
              </div>
            </div>
          </div>
        );

      case 'overlay-classic':
        return (
          <div className="relative flex h-full items-end p-8 min-h-0">
            <AdVisualBackground adImageUrl={media.adImageUrl} adImage={media.adImageUrl ?? null} autoEnhance={creative.autoEnhance} blurIntensity={creative.blurIntensity} specialEffect={creative.specialEffect} useBackgroundOnly={creative.useBackgroundOnly} primaryColor={primaryColor} styleKey="overlay-classic" getImageOverlayStyles={getImageOverlayStyles} />
            <div className="z-20 w-full">
              <div className={`rounded-xl p-6 ${creative.blurIntensity > 0 ? 'backdrop-blur-sm' : ''}`} style={creative.blurIntensity > 0 ? { backgroundColor: 'rgba(0, 0, 0, 0.4)' } : { backgroundColor: primaryColor }}>
                <h1 onClick={interactive ? () => onSelectElement?.('headline') : undefined} className={`${clickable('headline')} mb-3`} style={{ ...getTypographyStyle('headline', typography, 1, brandColors), lineHeight: 1.2, textAlign: creative.headlineAlign, color: creative.headlineColor || getTypographyStyle('headline', typography, 1, brandColors).color, textShadow: creative.headlineShadow ? '2px 2px 4px rgba(0,0,0,0.8)' : 'none', ...translateStyle(creative.headlineX, creative.headlineY), userSelect: 'none', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                  {headline}
                </h1>
                <p onClick={interactive ? () => onSelectElement?.('benefit') : undefined} className={`${clickable('benefit')} mb-2`} style={{ ...getTypographyStyle('benefit', typography, 1, brandColors), lineHeight: 1.5, textAlign: creative.benefitAlign, color: creative.benefitColor || getTypographyStyle('benefit', typography, 1, brandColors).color, textShadow: '1px 1px 2px rgba(0,0,0,0.8)', ...translateStyle(creative.benefitX, creative.benefitY), userSelect: 'none', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                  {benefit}
                </p>
                <p onClick={interactive ? () => onSelectElement?.('description') : undefined} className={`${clickable('description')} mb-4`} style={{ ...getTypographyStyle('description', typography, 1, brandColors), lineHeight: 1.4, textAlign: creative.descriptionAlign, color: creative.descriptionColor || getTypographyStyle('description', typography, 1, brandColors).color, textShadow: creative.descriptionShadow ? '1px 1px 2px rgba(0,0,0,0.8)' : 'none', ...translateStyle(creative.descriptionX, creative.descriptionY), userSelect: 'none', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                  {description}
                </p>
                <div onClick={interactive ? () => onSelectElement?.('cta') : undefined} className={`${clickable('cta')} w-full`} style={{ ...translateStyle(creative.ctaX, creative.ctaY), transformOrigin: 'center' }}>
                  <div className={`flex ${alignClass(creative.ctaAlign)}`}>
                    <button className="rounded-lg px-6 py-3 shadow-lg" style={{ backgroundColor: accentColor, ...getTypographyStyle('cta', typography, 1, brandColors) }}>
                      {callToAction}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'headline-impact':
        return (
          <div className="relative flex h-full flex-col justify-center p-8 min-h-0">
            <AdVisualBackground adImageUrl={media.adImageUrl} adImage={media.adImageUrl ?? null} autoEnhance={creative.autoEnhance} blurIntensity={creative.blurIntensity} specialEffect={creative.specialEffect} useBackgroundOnly={creative.useBackgroundOnly} primaryColor={primaryColor} styleKey="headline-impact" getImageOverlayStyles={getImageOverlayStyles} />
            {!creative.useBackgroundOnly && <div className="absolute inset-0 z-10" style={{ backgroundColor: primaryColor, opacity: 0.65 }} />}
            <div className="z-20 mx-auto w-full max-w-2xl">
              <div onClick={interactive ? () => onSelectElement?.('headline') : undefined} className={clickable('headline')} style={{ width: '100%', ...translateStyle(creative.headlineX, creative.headlineY), transformOrigin: 'center', textAlign: creative.headlineAlign }}>
                <h1 className="mb-8 font-bold" style={{ display: 'block', width: '100%', ...getTypographyStyle('headline', typography, 1.3, brandColors), fontSize: `${Math.max(8, Math.round(creative.headlineFontSize * 1.3))}px`, lineHeight: 1.1, textAlign: creative.headlineAlign, textShadow: creative.headlineShadow ? '3px 3px 6px rgba(0,0,0,0.7)' : 'none', maxHeight: '70vh', userSelect: 'none', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                  {headline}
                </h1>
              </div>
              <div onClick={interactive ? () => onSelectElement?.('benefit') : undefined} className={clickable('benefit')} style={{ width: '100%', ...translateStyle(creative.benefitX, creative.benefitY), transformOrigin: 'center', textAlign: creative.benefitAlign }}>
                <p className="mb-6" style={{ display: 'block', width: '100%', ...getTypographyStyle('benefit', typography, 1, brandColors), lineHeight: 1.5, textAlign: creative.benefitAlign, textShadow: creative.benefitShadow ? '1px 1px 2px rgba(0,0,0,0.7)' : 'none', userSelect: 'none', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                  {benefit}
                </p>
              </div>
              <div onClick={interactive ? () => onSelectElement?.('description') : undefined} className={clickable('description')} style={{ width: '100%', ...translateStyle(creative.descriptionX, creative.descriptionY), transformOrigin: 'center', textAlign: creative.descriptionAlign }}>
                <p className="mb-6" style={{ display: 'block', width: '100%', ...getTypographyStyle('description', typography, 1, brandColors), lineHeight: 1.4, textAlign: creative.descriptionAlign, textShadow: creative.descriptionShadow ? '1px 1px 2px rgba(0,0,0,0.7)' : 'none', userSelect: 'none', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                  {description}
                </p>
              </div>
              <div onClick={interactive ? () => onSelectElement?.('cta') : undefined} className={`${clickable('cta')} flex w-full ${alignClass(creative.ctaAlign)}`} style={{ ...translateStyle(creative.ctaX, creative.ctaY), transformOrigin: 'center', textAlign: creative.ctaAlign }}>
                <button className="rounded-lg px-8 py-4 shadow-lg" style={{ display: 'inline-block', backgroundColor: accentColor, ...getTypographyStyle('cta', typography, 1, brandColors), textShadow: creative.ctaShadow ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none' }}>
                  {callToAction}
                </button>
              </div>
            </div>
          </div>
        );

      case 'quote-bubble':
        return (
          <div className="relative flex h-full flex-col justify-center p-8 min-h-0">
            <AdVisualBackground adImageUrl={media.adImageUrl} adImage={media.adImageUrl ?? null} autoEnhance={creative.autoEnhance} blurIntensity={creative.blurIntensity} specialEffect={creative.specialEffect} useBackgroundOnly={creative.useBackgroundOnly} primaryColor={primaryColor} styleKey="quote-bubble" getImageOverlayStyles={getImageOverlayStyles} />
            <div className="z-20 mx-auto w-full max-w-lg">
              <div className={`relative mb-6 w-full min-w-0 rounded-2xl p-6 shadow-lg ${creative.blurIntensity > 0 ? 'backdrop-blur-sm' : ''}`} data-ad-text-content style={{ backgroundColor: creative.useBackgroundOnly ? '#FFFFFF' : hexToRgba(primaryColor, 0.85) }}>
                <div className="absolute left-8 overflow-hidden" style={{ width: '24px', height: '17px', bottom: '-15px', left: '2rem' }}>
                  <div className="h-6 w-6 rotate-45" style={{ backgroundColor: creative.useBackgroundOnly ? '#FFFFFF' : hexToRgba(primaryColor, 0.85), position: 'absolute', top: '-17px' }} />
                </div>
                <div onClick={interactive ? () => onSelectElement?.('headline') : undefined} className={clickable('headline')} style={{ width: '100%', ...translateStyle(creative.headlineX, creative.headlineY), transformOrigin: 'center', textAlign: creative.headlineAlign }}>
                  <h1 className="mb-4 text-gray-800" style={{ display: 'block', width: '100%', maxWidth: '100%', ...getTypographyStyle('headline', typography, 1, brandColors), lineHeight: 1.2, textAlign: creative.headlineAlign, color: creative.headlineColor || getTypographyStyle('headline', typography, 1, brandColors).color, textShadow: creative.headlineShadow ? '2px 2px 4px rgba(0,0,0,0.7)' : 'none', userSelect: 'none', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                    {headline}
                  </h1>
                </div>
                <div onClick={interactive ? () => onSelectElement?.('benefit') : undefined} className={clickable('benefit')} style={{ width: '100%', ...translateStyle(creative.benefitX, creative.benefitY), transformOrigin: 'center', textAlign: creative.benefitAlign }}>
                  <p className="mb-4" style={{ display: 'block', width: '100%', maxWidth: '100%', ...getTypographyStyle('benefit', typography, 1, brandColors), lineHeight: 1.4, textAlign: creative.benefitAlign, color: creative.benefitColor || getTypographyStyle('benefit', typography, 1, brandColors).color, textShadow: '1px 1px 2px rgba(0,0,0,0.8)', userSelect: 'none', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                    {benefit}
                  </p>
                </div>
                <div onClick={interactive ? () => onSelectElement?.('description') : undefined} className={clickable('description')} style={{ width: '100%', ...translateStyle(creative.descriptionX, creative.descriptionY), transformOrigin: 'center', textAlign: creative.descriptionAlign }}>
                <p className="mb-2" style={{ display: 'block', width: '100%', maxWidth: '100%', ...getTypographyStyle('description', typography, 1, brandColors), lineHeight: 1.4, textAlign: creative.descriptionAlign, color: creative.descriptionColor || getTypographyStyle('description', typography, 1, brandColors).color, textShadow: creative.descriptionShadow ? '1px 1px 2px rgba(0,0,0,0.7)' : 'none', userSelect: 'none', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                    {description}
                  </p>
                </div>
              </div>
              <div onClick={interactive ? () => onSelectElement?.('cta') : undefined} className={`${clickable('cta')} flex ${alignClass(creative.ctaAlign)}`} style={{ ...translateStyle(creative.ctaX, creative.ctaY), transformOrigin: 'center' }}>
                <button className="rounded-lg px-8 py-4 shadow-lg" style={{ backgroundColor: accentColor, ...getTypographyStyle('cta', typography, 1, brandColors), fontWeight: creative.ctaBold ? '700' : '600', textShadow: creative.ctaShadow ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none' }}>
                  {callToAction}
                </button>
              </div>
            </div>
          </div>
        );

      case 'full-visual':
        return (
          <div className="relative h-full">
            <AdVisualBackground adImageUrl={media.adImageUrl} adImage={media.adImageUrl ?? null} autoEnhance={creative.autoEnhance} blurIntensity={creative.blurIntensity} specialEffect={creative.specialEffect} useBackgroundOnly={creative.useBackgroundOnly} primaryColor={primaryColor} styleKey="full-visual" getImageOverlayStyles={getImageOverlayStyles} />
            <div className={`absolute top-4 z-20 ${creative.headlineAlign === 'center' ? 'left-1/2 -translate-x-1/2' : creative.headlineAlign === 'right' ? 'right-4' : 'left-4'}`} onClick={interactive ? () => onSelectElement?.('headline') : undefined}>
              <div style={{ width: '100%', ...translateStyle(creative.headlineX, creative.headlineY), transformOrigin: 'center', textAlign: creative.headlineAlign }}>
                <h1 id="ad-headline" className="font-black text-white" style={{ ...getTypographyStyle('headline', typography, 1, brandColors), color: creative.headlineColor || '#FFFFFF', lineHeight: 1.1, textAlign: creative.headlineAlign, textShadow: creative.headlineShadow ? '2px 2px 4px rgba(0,0,0,0.8)' : 'none', userSelect: 'none', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                  {headline}
                </h1>
              </div>
            </div>
            <div id="ad-bottom-content" className="absolute bottom-4 left-4 right-4 z-20 flex items-end">
              <div id="ad-benefit-description-container" className="max-w-xs pr-6" style={{ width: '320px', minWidth: '320px', flexShrink: 0 }}>
                <div onClick={interactive ? () => onSelectElement?.('benefit') : undefined} className={clickable('benefit')} style={{ width: '100%', ...translateStyle(creative.benefitX, creative.benefitY), transformOrigin: 'center', textAlign: creative.benefitAlign }}>
                  <p id="ad-benefit" className="text-white" style={{ ...getTypographyStyle('benefit', typography, 1, brandColors), color: creative.benefitColor || '#FFFFFF', lineHeight: 1.3, textShadow: creative.benefitShadow ? '1px 1px 2px rgba(0,0,0,0.8)' : 'none', userSelect: 'none', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                    {benefit}
                  </p>
                </div>
                <div onClick={interactive ? () => onSelectElement?.('description') : undefined} className={clickable('description')} style={{ width: '100%', ...translateStyle(creative.descriptionX, creative.descriptionY), transformOrigin: 'center', textAlign: creative.descriptionAlign }}>
                  <p id="ad-description" className="mb-2 text-white" style={{ ...getTypographyStyle('description', typography, 1, brandColors), color: creative.descriptionColor || '#FFFFFF', lineHeight: 1.3, textShadow: creative.descriptionShadow ? '1px 1px 2px rgba(0,0,0,0.8)' : 'none', userSelect: 'none', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                    {description}
                  </p>
                </div>
              </div>
              <div id="ad-cta-container" className="ml-auto" onClick={interactive ? () => onSelectElement?.('cta') : undefined}>
                <div className={`flex ${alignClass(creative.ctaAlign)}`} style={{ ...translateStyle(creative.ctaX, creative.ctaY), transformOrigin: 'center', textAlign: creative.ctaAlign }}>
                  <button id="ad-cta" className="rounded px-3 py-2 font-bold" style={{ backgroundColor: accentColor || '#1F2937', ...getTypographyStyle('cta', typography, 1, brandColors), color: creative.ctaColor || '#FFFFFF' }}>
                    {callToAction}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="relative z-10 flex h-full flex-col items-center justify-center p-8 min-h-0">
            <div className="w-full max-w-lg text-center">
              <h1 onClick={interactive ? () => onSelectElement?.('headline') : undefined} className={`${clickable('headline')} mb-6`} style={{ ...getTypographyStyle('headline', typography, 1, brandColors), lineHeight: 1.2, textShadow: '2px 2px 4px rgba(0,0,0,0.7)', userSelect: 'none' }}>{headline}</h1>
              <p onClick={interactive ? () => onSelectElement?.('benefit') : undefined} className={`${clickable('benefit')} mb-4`} style={{ ...getTypographyStyle('benefit', typography, 1, brandColors), lineHeight: 1.5, textShadow: '1px 1px 2px rgba(0,0,0,0.7)', userSelect: 'none' }}>{benefit}</p>
              <p onClick={interactive ? () => onSelectElement?.('description') : undefined} className={`${clickable('description')} mb-8 opacity-90`} style={{ ...getTypographyStyle('description', typography, 1, brandColors), lineHeight: 1.4, textShadow: creative.descriptionShadow ? '1px 1px 2px rgba(0,0,0,0.7)' : 'none', userSelect: 'none' }}>{description}</p>
              <div onClick={interactive ? () => onSelectElement?.('cta') : undefined} className={`${clickable('cta')} flex ${alignClass(creative.ctaAlign)}`}>
                <button className="rounded-lg px-8 py-4 shadow-lg" style={{ backgroundColor: accentColor, ...getTypographyStyle('cta', typography, 1, brandColors), textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>{callToAction}</button>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div ref={wrapperRef} className="flex h-full w-full items-center justify-center overflow-hidden">
      <div
        id="final-ad-canvas"
        data-zone-a="true"
        ref={canvasRef}
        className="relative h-[720px] w-[640px] flex-shrink-0 overflow-hidden rounded-xl bg-white shadow-lg"
        style={{ transform: `scale(${previewScale})`, transformOrigin: 'center center', boxSizing: 'border-box' }}
      >
        <div
          data-ad-canvas="true"
          className="relative h-full w-full min-h-0 min-w-0 overflow-hidden"
          style={{
            backgroundColor: creative.useBackgroundOnly ? (primaryColor || '#0066FF') : '#FFFFFF',
            backgroundImage: !creative.useBackgroundOnly && media.adImageUrl ? `url(${media.adImageUrl})` : undefined,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            fontFamily: creative.selectedFont,
            boxShadow: creative.specialEffect === 'shadow' ? '0 25px 50px -12px rgba(0,0,0,0.25), inset 0 0 0 1px rgba(0,0,0,0.1)' : undefined,
            filter: creative.autoEnhance && media.adImageUrl ? 'brightness(1.2) contrast(1.1) saturate(1.1)' : 'none',
          }}
        >
          {media.overlayImageUrl && (
            <div
              className="absolute"
              onPointerDown={interactive ? onOverlayPointerDown : undefined}
              style={{ left: `${creative.overlayPosition.x}%`, top: `${creative.overlayPosition.y}%`, width: `${creative.overlaySize}%`, height: 'auto', transform: 'translate(-50%, -50%)', maxWidth: 800, maxHeight: 800, zIndex: 15, cursor: 'grab', touchAction: 'none' }}
            >
              <img src={media.overlayImageUrl} crossOrigin="anonymous" alt="" className="h-auto w-full object-contain" style={{ filter: creative.autoEnhance ? 'brightness(1.2) contrast(1.1) saturate(1.1)' : 'none' }} />
            </div>
          )}
          {media.logoUrl && (
            <div
              className="absolute"
              onPointerDown={interactive ? onLogoPointerDown : undefined}
              style={{ left: `${creative.logoPosition.x}%`, top: `${creative.logoPosition.y}%`, transform: 'translate(-50%, -50%)', zIndex: 16, width: `${creative.logoSize}%`, cursor: 'grab', touchAction: 'none' }}
            >
              <img src={media.logoUrl} crossOrigin="anonymous" alt="" className="h-auto w-full object-contain" />
            </div>
          )}
          {renderLayout()}
        </div>
      </div>
    </div>
  );
};

export default InternalAdCanvasView;
